import { KeyRound, LoaderCircle, Mail, ShieldCheck, X } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import type { AuthPort } from "../auth-port";
import { useI18n } from "../i18n";

export function AccountDialog({
  auth,
  onClose,
  onSignOut,
}: {
  auth: AuthPort;
  onClose: () => void;
  onSignOut: () => Promise<void>;
}) {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verified, setVerified] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);
  const [signingOut, setSigningOut] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    auth.accountInfo().then((account) => {
      if (active) setEmail(account.email);
    }).catch((cause) => {
      if (active) setError(cause instanceof Error ? cause.message : t("app.account.updateFailed"));
    });
    return () => { active = false; };
  }, [auth, t]);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = window.setInterval(() => setResendSeconds((value) => Math.max(0, value - 1)), 1000);
    return () => window.clearInterval(timer);
  }, [resendSeconds]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || signingOut) return;
    setError(null);
    setMessage(null);
    if (!verified) {
      setError(t("app.account.verifyFirst"));
      return;
    }
    if (password !== confirmPassword) {
      setError(t("app.account.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await auth.completePasswordReset({ password });
      setPassword("");
      setConfirmPassword("");
      setMessage(t("app.account.passwordUpdated"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.account.updateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const sendCode = async (): Promise<boolean> => {
    if (busy || resendSeconds > 0) return false;
    setBusy(true);
    setError(null);
    try {
      const challenge = await auth.requestEmailCode(email, "reset_password");
      setChallengeId(challenge.challengeId);
      setCode("");
      setResendSeconds(60);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.account.updateFailed"));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    if (busy || !challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await auth.verifyPasswordReset({ challengeId, email, code });
      setVerified(true);
      setCode("");
      setMessage(t("app.account.codeVerified"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t("app.account.updateFailed"));
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    if (busy || resendSeconds > 0) return;
    if (await sendCode()) setMessage(t("app.auth.codeResent"));
  };

  const signOut = async () => {
    if (busy || signingOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
      onClose();
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <div
      className="dialog-backdrop account-dialog-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t("app.account.title")}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="account-dialog">
        <header>
          <span className="account-dialog-icon"><ShieldCheck size={18} /></span>
          <div>
            <h2>{t("app.account.title")}</h2>
            <p>{t("app.account.lead")}</p>
          </div>
          <button type="button" onClick={onClose} aria-label={t("common.close")}><X size={16} /></button>
        </header>
        <form onSubmit={submit}>
          <label>
            <span>{t("app.auth.email")}</span>
            <input type="email" value={email} readOnly autoComplete="email" disabled={busy || signingOut} />
            <small>{t("app.account.codeDetail")}</small>
          </label>
          {!verified ? <label>
            <span>{t("app.auth.verificationCode")}</span>
            <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder={t("app.auth.codePlaceholder")} autoComplete="one-time-code" disabled={busy || !challengeId} />
            {challengeId ? <div className="account-dialog-code-actions">
              <small>{t("app.auth.codeSent")}</small>
              <button type="button" className="account-dialog-resend" onClick={() => void resendCode()} disabled={busy || resendSeconds > 0}>
                {resendSeconds > 0 ? t("app.auth.resendIn", { seconds: resendSeconds }) : t("app.auth.resendCode")}
              </button>
            </div> : null}
          </label> : null}
          {verified ? <>
          <label>
            <span>{t("app.account.newPassword")}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t("app.auth.passwordPlaceholder")}
              autoComplete="new-password"
              minLength={10}
              required
              disabled={busy || signingOut}
            />
          </label>
          <label>
            <span>{t("app.account.confirmPassword")}</span>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder={t("app.auth.confirmPasswordPlaceholder")}
              autoComplete="new-password"
              minLength={10}
              required
              disabled={busy || signingOut}
            />
          </label>
          </> : null}
          {message ? <p className="account-dialog-message" role="status">{message}</p> : null}
          {error ? <p className="account-dialog-error" role="alert">{error}</p> : null}
          <div className="account-dialog-actions">
            <button type="button" className="ghost-button" onClick={() => void signOut()} disabled={busy || signingOut}>
              {signingOut ? <LoaderCircle className="spin" size={14} /> : null}
              {t("app.auth.signOut")}
            </button>
            {!verified ? (
              <button type="button" className="primary-button" onClick={() => void (challengeId ? verifyCode() : sendCode())} disabled={busy || signingOut || (challengeId ? code.length !== 6 : resendSeconds > 0)}>
                {busy ? <LoaderCircle className="spin" size={14} /> : <Mail size={14} />}
                {challengeId ? t("app.account.verifyCode") : resendSeconds > 0 ? t("app.auth.resendIn", { seconds: resendSeconds }) : t("app.account.sendCode")}
              </button>
            ) : (
              <button type="submit" className="primary-button" disabled={busy || signingOut}>
                {busy ? <LoaderCircle className="spin" size={14} /> : <KeyRound size={14} />}
                {t("app.account.updatePassword")}
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
